import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Receipt, FileText, ExternalLink, Search, Wrench, Link2, Unlink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => (v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATO_COLORS = {
  bozza: 'bg-slate-100 text-slate-700 border-slate-300',
  inviato: 'bg-blue-100 text-blue-700 border-blue-300',
  fatturato: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  parzialmente_fatturato: 'bg-amber-100 text-amber-700 border-amber-300',
  pagato: 'bg-emerald-200 text-emerald-800 border-emerald-400',
  annullato: 'bg-red-100 text-red-700 border-red-300',
};

const STATO_LABELS = {
  bozza: 'Bozza',
  inviato: 'Inviato',
  fatturato: 'Fatturato',
  parzialmente_fatturato: 'Parzialmente Fatturato',
  pagato: 'Pagato',
  annullato: 'Annullato',
};

export default function ContiEmessiPage() {
  const [data, setData] = useState({ items: [], count: 0, totale_lordo_complessivo: 0, totali_per_stato: {} });
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState('');
  // Supporto deep-link ?q=NUMCONTO per ricerca pre-popolata (iter54)
  const initialQuery = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') || '' : '';
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => { fetchAll(); }, [filtroStato]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filtroStato ? `?stato=${filtroStato}` : '';
      const res = await axios.get(`${API}/conti-emessi/all${params}`, { withCredentials: true });
      setData(res.data || { items: [], count: 0, totale_lordo_complessivo: 0, totali_per_stato: {} });
    } catch {
      toast.error('Errore nel caricamento dei conti emessi');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!query.trim()) return data.items;
    const q = query.toLowerCase();
    return data.items.filter((c) =>
      (c.numero || '').toLowerCase().includes(q) ||
      (c.cantiere_nome || '').toLowerCase().includes(q) ||
      (c.cliente_nome || '').toLowerCase().includes(q) ||
      (c.descrizione || '').toLowerCase().includes(q)
    );
  }, [data.items, query]);

  // iter56: ripara link mancanti tra conti emessi e fatture (per dati pre-iter45)
  const [repairing, setRepairing] = useState(false);
  // iter57: dialog Collega Fattura
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkConto, setLinkConto] = useState(null);
  const [linkFatture, setLinkFatture] = useState([]);
  const [linkFatturaId, setLinkFatturaId] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkCantieriMap, setLinkCantieriMap] = useState({});

  const openLinkDialog = async (conto) => {
    setLinkConto(conto);
    setLinkFatturaId(conto.fattura_uscita_id || '');
    setLinkOpen(true);
    setLinkFatture([]);
    try {
      // Tutte le fatture (tipo fattura, no NC, non annullate) — collegabili anche
      // a cantieri diversi da quello del conto. Stesso cantiere mostrato per primo.
      const [resF, resC] = await Promise.all([
        axios.get(`${API}/fatture-uscita`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true }),
      ]);
      setLinkCantieriMap(Object.fromEntries((resC.data || []).map(c => [c.id, c.name])));
      const list = (resF.data || []).filter(f =>
        (f.tipo_documento || 'fattura') === 'fattura' &&
        !['annullata'].includes(f.stato)
      );
      list.sort((a, b) => {
        const aSame = a.cantiere_id === conto.cantiere_id ? 0 : 1;
        const bSame = b.cantiere_id === conto.cantiere_id ? 0 : 1;
        if (aSame !== bSame) return aSame - bSame;
        return (b.data || '').localeCompare(a.data || '');
      });
      setLinkFatture(list);
    } catch {
      toast.error('Errore caricamento fatture');
    }
  };

  const saveLink = async () => {
    if (!linkConto) return;
    setLinking(true);
    try {
      // Se l'utente vuole COLLEGARE: chiama link-conto su quella fattura
      if (linkFatturaId) {
        await axios.post(
          `${API}/fatture-uscita/${linkFatturaId}/link-conto`,
          { conto_emesso_id: linkConto.id },
          { withCredentials: true }
        );
        toast.success('Fattura collegata al conto');
      } else {
        // SCOLLEGARE: scollega la fattura attualmente legata (se esiste)
        if (linkConto.fattura_uscita_id) {
          await axios.post(
            `${API}/fatture-uscita/${linkConto.fattura_uscita_id}/link-conto`,
            { conto_emesso_id: null },
            { withCredentials: true }
          );
          toast.success('Collegamento rimosso');
        }
      }
      setLinkOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore collegamento');
    } finally {
      setLinking(false);
    }
  };

  const handleRepairLinks = async () => {
    if (!window.confirm('Ripara i collegamenti mancanti tra Conti Emessi e Fatture Emesse?\\n\\nUtile se hai notato che il badge "Da Conto …" non appare su alcune fatture vecchie. Idempotente: si può rieseguire più volte senza danni.')) return;
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/conti-emessi/backfill-links`, {}, { withCredentials: true });
      const d = res.data;
      toast.success(`Riparati: ${d.fatture_aggiornate} link · già OK: ${d.gia_collegati} · orfani (fatture cancellate): ${d.orfani}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore riparazione');
    } finally {
      setRepairing(false);
    }
  };

  // iter55: cambio stato inline
  const handleChangeStato = async (conto, nuovoStato) => {
    if (nuovoStato === conto.stato) return;
    if (conto.stato === 'fatturato' && nuovoStato !== 'fatturato') {
      if (!window.confirm('Questo conto è già stato fatturato. Cambiando stato la fattura collegata NON viene rimossa automaticamente. Procedere?')) return;
    }
    try {
      await axios.put(
        `${API}/cantieri/${conto.cantiere_id}/conti-emessi/${conto.id}`,
        { stato: nuovoStato },
        { withCredentials: true }
      );
      toast.success(`Stato aggiornato: ${STATO_LABELS[nuovoStato] || nuovoStato}`);
      // Optimistic update locale
      setData((prev) => ({
        ...prev,
        items: prev.items.map((c) => c.id === conto.id ? { ...c, stato: nuovoStato } : c),
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento stato');
      fetchAll();
    }
  };

  const totDaFatturare = data.totale_da_fatturare ?? 0;
  const totFatturato = data.totale_fatturato ?? 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="conti-emessi-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Conti Emessi</h1>
          <p className="text-slate-500 mt-1">Documenti preparatori emessi ai clienti — pronti per essere trasformati in fattura attiva</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRepairLinks}
          disabled={repairing}
          className="text-amber-700 border-amber-300 hover:bg-amber-50"
          data-testid="repair-links-btn"
          title="Ripara i collegamenti mancanti con le fatture (per dati storici)"
        >
          <Wrench className="h-3.5 w-3.5 mr-1.5" />
          {repairing ? 'Riparazione…' : 'Ripara link fatture'}
        </Button>
      </div>

      {/* iter100q: Riepilogo separato — Da Fatturare vs Fatturato (no somma cumulativa) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-amber-700">
              <Receipt className="h-4 w-4" />
              <p className="text-xs uppercase font-semibold tracking-wide">Da Fatturare — per anno</p>
            </div>
            {(data.per_anno || []).filter((r) => r.da_fatturare > 0).length === 0 ? (
              <p className="text-3xl font-bold text-amber-800 mt-1" data-testid="kpi-da-fatturare">€ {fmt(totDaFatturare)}</p>
            ) : (
              <div className="mt-2 space-y-1" data-testid="da-fatturare-per-anno">
                {(data.per_anno || []).filter((r) => r.da_fatturare > 0).map((r) => (
                  <div key={r.anno} className="flex items-center justify-between" data-testid={`da-fatturare-anno-${r.anno}`}>
                    <span className="text-sm text-amber-700">{r.anno || 'n/d'} <span className="text-amber-500 text-xs">({r.count_da_fatturare ?? r.count})</span></span>
                    <span className="font-mono font-bold text-amber-800">€ {fmt(r.da_fatturare)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-amber-200 pt-1 mt-1">
                  <span className="text-xs uppercase text-amber-600 font-semibold">Totale</span>
                  <span className="font-mono font-bold text-amber-900" data-testid="kpi-da-fatturare">€ {fmt(totDaFatturare)}</span>
                </div>
              </div>
            )}
            <p className="text-xs text-amber-700 mt-1">Importo ancora da collegare a fatture</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-emerald-700">
              <FileText className="h-4 w-4" />
              <p className="text-xs uppercase font-semibold tracking-wide">Fatturato</p>
            </div>
            <p className="text-3xl font-bold text-emerald-800 mt-1" data-testid="kpi-fatturato">€ {fmt(totFatturato)}</p>
            <p className="text-xs text-emerald-700 mt-1">Importo già collegato a fatture emesse</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              data-testid="conti-emessi-search"
              placeholder="Cerca per numero, cantiere, cliente…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-64"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant={filtroStato === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltroStato('')}
              data-testid="filter-tutti"
            >Tutti</Button>
            <Button
              variant={filtroStato === 'bozza,inviato' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltroStato('bozza,inviato')}
              data-testid="filter-da-fatturare"
            >Da Fatturare</Button>
            <Button
              variant={filtroStato === 'fatturato' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFiltroStato('fatturato')}
              data-testid="filter-fatturato"
            >Già Fatturati</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Receipt className="h-5 w-5 text-slate-600" />
            {filteredItems.length} conti emessi
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase">N. Conto</TableHead>
                <TableHead className="text-xs uppercase">Data</TableHead>
                <TableHead className="text-xs uppercase">Cantiere</TableHead>
                <TableHead className="text-xs uppercase">Cliente</TableHead>
                <TableHead className="text-xs uppercase text-right">Imponibile</TableHead>
                <TableHead className="text-xs uppercase text-right">IVA</TableHead>
                <TableHead className="text-xs uppercase text-right">Totale</TableHead>
                <TableHead className="text-xs uppercase">Stato</TableHead>
                <TableHead className="text-xs uppercase">Fattura</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-500">Nessun conto emesso trovato</TableCell></TableRow>
              ) : filteredItems.map((c) => (
                <TableRow key={c.id} data-testid={`conto-emesso-row-${c.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{c.numero}</TableCell>
                  <TableCell className="text-sm">{c.data_emissione ? format(parseISO(c.data_emissione), 'dd MMM yyyy', { locale: it }) : '—'}</TableCell>
                  <TableCell className="text-sm">{c.cantiere_nome || '—'}</TableCell>
                  <TableCell className="text-sm">{c.cliente_nome || '—'}</TableCell>
                  <TableCell className="text-right text-sm font-mono">{fmt(c.prezzo_emesso)}</TableCell>
                  <TableCell className="text-right text-xs text-slate-500 font-mono">{c.iva_pct ? `${c.iva_pct}%` : '—'}</TableCell>
                  <TableCell className="text-right font-bold font-mono">{fmt(c.totale_lordo || c.prezzo_emesso)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <select
                        value={c.stato}
                        onChange={(e) => handleChangeStato(c, e.target.value)}
                        className={`text-xs font-medium px-2 py-1 rounded-md border cursor-pointer outline-none ${STATO_COLORS[c.stato] || ''}`}
                        data-testid={`stato-conto-${c.id}`}
                      >
                        <option value="bozza">Bozza</option>
                        <option value="inviato">Inviato</option>
                        <option value="fatturato">Fatturato</option>
                        <option value="pagato">Pagato</option>
                        <option value="annullato">Annullato</option>
                      </select>
                      {c.stato_fatturazione === 'parzialmente_fatturato' && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${STATO_COLORS.parzialmente_fatturato}`} data-testid={`stato-fatt-${c.id}`}>
                          Parz. Fatturato
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(c.num_fatture_collegate || 0) > 0 ? (
                      <div className="space-y-1">
                        {(c.fatture_collegate || []).map((f, idx) => (
                          <div key={f.fattura_id} className="flex items-center gap-1">
                            <a
                              href={`/admin/fatturazione/fatture-emesse?q=${encodeURIComponent(f.numero || '')}`}
                              title={`Apri Fattura ${f.numero}`}
                              data-testid={`link-fattura-${c.id}-${idx}`}
                            >
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs hover:bg-emerald-100 cursor-pointer">
                                <FileText className="h-3 w-3 mr-1" />{f.numero}
                                <span className="ml-1 text-[10px] font-mono opacity-70">· € {fmt(f.importo)}</span>
                              </Badge>
                            </a>
                          </div>
                        ))}
                        {c.stato_fatturazione === 'parzialmente_fatturato' && (
                          <div className="mt-1 rounded-md bg-amber-50 border border-amber-200 p-1.5 text-[11px]">
                            <div className="flex items-center justify-between gap-2 text-amber-900">
                              <span>Fatturato:</span>
                              <span className="font-mono font-semibold">€ {fmt(c.importo_fatturato)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2 text-amber-900">
                              <span>Rimanente:</span>
                              <span className="font-mono font-semibold">€ {fmt(c.importo_da_fatturare)}</span>
                            </div>
                            <div className="mt-1 h-1.5 bg-amber-100 rounded overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, ((c.importo_fatturato || 0) / (c.totale_lordo || c.prezzo_emesso || 1)) * 100)}%` }} />
                            </div>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[10px] px-1 text-slate-500 hover:text-amber-600"
                          onClick={() => openLinkDialog(c)}
                          title="Aggiungi/Modifica collegamento"
                          data-testid={`edit-link-${c.id}`}
                        >
                          <Link2 className="h-3 w-3 mr-1" />
                          {c.stato_fatturazione === 'parzialmente_fatturato' ? 'Aggiungi fattura' : 'Modifica'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-amber-200 text-amber-700 hover:bg-amber-50"
                        onClick={() => openLinkDialog(c)}
                        data-testid={`link-conto-${c.id}`}
                      >
                        <Link2 className="h-3 w-3 mr-1" />Collega Fattura
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <a
                      href={`/admin/contabilita?cantiere=${c.cantiere_id}`}
                      title={`Apri Contabilità di ${c.cantiere_nome || 'cantiere'}`}
                      data-testid={`open-cantiere-${c.id}`}
                    >
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-600 hover:bg-slate-100">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* iter57: Dialog Collega Fattura al Conto Emesso */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-xl" data-testid="link-fattura-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-600" />
              Collega Fattura al Conto Emesso
            </DialogTitle>
          </DialogHeader>
          {linkConto && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded-md p-3 text-sm">
                <p><strong>Conto:</strong> <span className="font-mono">{linkConto.numero}</span></p>
                <p><strong>Cantiere:</strong> {linkConto.cantiere_nome}</p>
                <p><strong>Cliente:</strong> {linkConto.cliente_nome}</p>
                <p><strong>Totale:</strong> € {fmt(linkConto.totale_lordo || linkConto.prezzo_emesso)}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Fattura Emessa di riferimento</label>
                <Select value={linkFatturaId || 'none'} onValueChange={v => setLinkFatturaId(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="link-fattura-select">
                    <SelectValue placeholder="Seleziona fattura…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessun collegamento —</SelectItem>
                    {linkFatture.length === 0 ? (
                      <SelectItem value="empty" disabled>Nessuna fattura disponibile</SelectItem>
                    ) : linkFatture.map(f => {
                      const linkedToOther = f.conto_emesso_id && f.conto_emesso_id !== linkConto.id;
                      const otherCantiere = f.cantiere_id !== linkConto.cantiere_id;
                      return (
                        <SelectItem key={f.id} value={f.id} disabled={linkedToOther}>
                          <span className="font-mono">{f.numero}</span>
                          {f.protocollo && <span className="text-xs ml-1 opacity-70">({f.protocollo})</span>}
                          {' · '}{f.data && format(parseISO(f.data), 'dd-MM-yyyy')}
                          {' · €'}{(f.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          {otherCantiere && ` · ${linkCantieriMap[f.cantiere_id] || f.cliente || 'altro cantiere'}`}
                          {linkedToOther && ` (collegato a ${f.conto_emesso_numero})`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {linkConto.fattura_uscita_id && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-1.5">
                    <Unlink className="h-3.5 w-3.5" />
                    Attualmente collegato a: <strong>{linkConto.fattura_uscita_numero}</strong>. Cambia o seleziona &quot;Nessun collegamento&quot; per scollegare.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setLinkOpen(false)} disabled={linking}>Annulla</Button>
                <Button
                  onClick={saveLink}
                  disabled={linking}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                  data-testid="save-link-btn"
                >
                  {linking ? 'Salvataggio…' : 'Salva collegamento'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
